import { Form, FormItem, Input } from 'formik-antd';
import styled, { css } from 'styled-components';
import { LockOutlined, UserOutlined } from '@ant-design/icons';
import { Button, Card, Typography } from 'antd';
import { FormikProps } from 'formik';
import * as React from 'react';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { LogoBlackIcon } from 'common/components/Logo';
import { PasswordEyeIcon } from 'common/components/PasswordEyeIcon';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

import { Redirect } from 'react-router';

const { Title } = Typography;

export interface FormikValues {
  password: string;
  username: string;
}

export interface LoginFormProps {
  loading: boolean;
}

const Header = styled.div`
  display: grid;
  grid-template-columns: min-content min-content;
  grid-gap: 0.5rem;
  white-space: nowrap;
  justify-content: center;
  align-items: center;
`;

export const DemoTitle = styled('span')(
  () => css`
    color: rgb(57, 192, 254);
    margin-left: 0.4rem;
  `
);

export const HigoLogo = styled(LogoBlackIcon)(
  () => css`
    margin-bottom: 0.5rem;
  `
);

const StyledLink = styled(Link)`
  float: right;
  margin-top: 1rem;
  font-size: 13px;
`;

const PasswordVisibilityToggle = styled.span<{ isVisible: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  cursor: pointer;
  color: rgba(0, 0, 0, 0.45);
  position: relative;

  &:hover {
    color: rgba(0, 0, 0, 0.65);
  }

  &::after {
    content: '';
    position: absolute;
    width: 18px;
    height: 2px;
    background-color: currentColor;
    transform: rotate(-35deg);
    opacity: ${({ isVisible }) => (isVisible ? 0 : 1)};
    transition: opacity 0.2s ease-in-out;
  }
`;

type LoginFormOwnProps = FormikProps<FormikValues> & LoginFormProps;

export const LoginForm: React.FC<LoginFormOwnProps> = (props) => {
  const intlMessage = useFormatMessage();
  const [isPasswordVisible, setIsPasswordVisible] = React.useState(false);

  const onResetClick = () => {
    return <Redirect to={`/reset-password`} />;
  };

  const isDemoEnvironment = process.env.DEMO_ENV === 'true';

  return (
    <Form style={{ width: '25%' }}>
      <Header id="login-header">
        <HigoLogo />
        <Title level={2}>
          Admin Panel {isDemoEnvironment && <DemoTitle>Demo</DemoTitle>}
        </Title>
      </Header>

      <Card>
        <FormItem name="username">
          <Input
            name="username"
            prefix={<UserOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
            placeholder={intlMessage({
              id: 'auth.username',
              defaultMessage: 'Username',
            })}
          />
        </FormItem>

        <FormItem name="password">
          <Input
            name="password"
            prefix={<LockOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
            type={isPasswordVisible ? 'text' : 'password'}
            suffix={
              <PasswordVisibilityToggle
                isVisible={isPasswordVisible}
                role="button"
                tabIndex={0}
                aria-label={
                  isPasswordVisible
                    ? intlMessage({
                        id: 'auth.hidePassword',
                        defaultMessage: 'Hide password',
                      })
                    : intlMessage({
                        id: 'auth.showPassword',
                        defaultMessage: 'Show password',
                      })
                }
                onClick={() => setIsPasswordVisible((prev) => !prev)}
                onMouseDown={(event) => event.preventDefault()}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setIsPasswordVisible((prev) => !prev);
                  }
                }}
              >
                <PasswordEyeIcon />
              </PasswordVisibilityToggle>
            }
            placeholder={intlMessage({
              id: 'auth.password',
              defaultMessage: 'Password',
            })}
          />
        </FormItem>

        <Button
          id="button-login"
          htmlType="submit"
          loading={props.loading}
          style={{ width: '100%', backgroundColor: 'rgb(57, 192, 254)' }}
          type="primary"
        >
          {props.loading && intlMessage({ id: 'loading' })}
          {!props.loading && intlMessage({ id: 'auth.login' })}
        </Button>

        <StyledLink onClick={onResetClick} to={`/forget-password`}>
          <FormattedMessage id="authentication.forgotPassword" />
        </StyledLink>
      </Card>
    </Form>
  );
};
